import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { gql, useApolloClient, useQuery } from "@apollo/client";
import AdminLayout from "../../Layout/index.jsx";
import LightSpinner from "../../../../components/LightSpinner.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { FaDownload } from "react-icons/fa6";
import { LuX } from "react-icons/lu";
import { MdTableChart } from "react-icons/md";
const LEETCODE_BULK_QUERY = gql`
  query GetLeetcodeBulk($usernames: [String!]!) {
    leetcodeBulk(usernames: $usernames) {
      username
      name
      ranking
      totalSolved
      easySolved
      mediumSolved
      hardSolved
      
      profileUrl
    }
  }
`;

const GET_CLUSTER_STUDENTS = gql`
  query GetClusterStudents($clusterId: String!) {
    getClusterStudents(clusterId: $clusterId) {
      id
      name
      email
      rollNo
      leetCodeUname
    }
  }
`;

const LEETCODE_ACCEPTED_QUESTIONS_QUERY = gql`
  query LeetcodeAcceptedQuestions(
    $username: String!
    $fromDate: String
    $toDate: String
    $days: Int
  ) {
    leetcodeAcceptedQuestions(
      username: $username
      fromDate: $fromDate
      toDate: $toDate
      days: $days
    ) {
      username
      totalCount
      fromDate
      toDate
      days {
        date
        count
        questions {
          title
          titleSlug
          date
        }
      }
    }
  }
`;

const ACTIVITY_FILTERS = [
  { value: "ALL_TIME", label: "All Time" },
  { value: "7", label: "Last 7 Days" },
  { value: "10", label: "Last 10 Days" },
  { value: "28", label: "Last 28 Days" },
  { value: "90", label: "Last 90 Days" },
  { value: "CUSTOM", label: "Custom Range" },
];

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildPresetRange = (daysValue) => {
  const days = Number(daysValue);
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));

  return {
    fromDate: formatLocalDate(start),
    toDate: formatLocalDate(end),
  };
};

const LeetcodeReportDetails = () => {
  const { clusterId } = useParams();
  const apolloClient = useApolloClient();

  const isValidLeetCodeUsername = (username) => {
    const normalized = (username || "").trim();
    return /^[a-zA-Z0-9_-]{3,40}$/.test(normalized);
  };

  const [selectedMode, setSelectedMode] = useState("ALL");
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [usernames, setUsernames] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [activityFilter, setActivityFilter] = useState("ALL_TIME");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [completedStats, setCompletedStats] = useState({});
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [questionsModalUsername, setQuestionsModalUsername] = useState("");
  const [questionsPopupPos, setQuestionsPopupPos] = useState(null);
  const [showCompareList, setShowCompareList] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [extractedSlugs, setExtractedSlugs] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);

  const { data: studentData } = useQuery(GET_CLUSTER_STUDENTS, {
    variables: { clusterId },
    skip: !clusterId,
  });

  const { data, loading } = useQuery(LEETCODE_BULK_QUERY, {
    variables: { usernames },
    skip: usernames.length === 0,
  });

  const processedData = useMemo(() => {
   
    return data?.leetcodeBulk || [];
  }, [data]);

  const displayedData = useMemo(() => {
    return processedData.filter((item) => item?.username);
  }, [processedData]);

  const fetchableStudents = useMemo(() => {
    return (studentData?.getClusterStudents || []).filter((student) =>
      isValidLeetCodeUsername(student.leetCodeUname)
    );
  }, [studentData]);

  const isFilteredView = activityFilter !== "ALL_TIME";

  const searchedData = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) {
      return displayedData;
    }

    return displayedData.filter((item) => {
      const matchedStudent =
        studentData?.getClusterStudents?.find(
          (st) => st.leetCodeUname === item.username
        ) || {};

      const studentName = (matchedStudent.name || item.name || "")
        .toString()
        .toLowerCase();
      const rollNo = (matchedStudent.rollNo || "").toString().toLowerCase();

      return studentName.includes(query) || rollNo.includes(query);
    });
  }, [displayedData, searchText, studentData]);

  const activeReportUsernames = useMemo(() => {
    const names = displayedData
      .map((item) => item?.username)
      .filter(Boolean);

    return [...new Set(names)];
  }, [displayedData]);

  const modalQuestions = useMemo(() => {
    if (!questionsModalUsername) {
      return [];
    }

    return completedStats[questionsModalUsername]?.questions || [];
  }, [completedStats, questionsModalUsername]);

  // ✅ Auto fetch ALL when selected
  useEffect(() => {
    if (selectedMode === "ALL" && studentData?.getClusterStudents?.length) {
      const allUsernames = [...new Set(fetchableStudents.map((s) => s.leetCodeUname.trim()))];
     
      setUsernames(allUsernames);
      setShowReport(true);
    }
  }, [selectedMode, studentData, fetchableStudents]);

  useEffect(() => {
    setSelectedStudentIds((prev) =>
      prev.filter((id) => fetchableStudents.some((student) => student.id === id))
    );
  }, [fetchableStudents]);

  useEffect(() => {
    if (!showReport || activityFilter === "ALL_TIME") {
      setCompletedStats({});
      setQuestionsModalUsername("");
      setQuestionsPopupPos(null);
      setActivityLoading(false);
      setActivityError("");
      return;
    }

    if (!activeReportUsernames.length) {
      setCompletedStats({});
      setQuestionsModalUsername("");
      setQuestionsPopupPos(null);
      setActivityLoading(false);
      return;
    }

    if (activityFilter === "CUSTOM" && (!customFromDate || !customToDate)) {
      setCompletedStats({});
      setQuestionsModalUsername("");
      setQuestionsPopupPos(null);
      setActivityLoading(false);
      return;
    }

    let ignore = false;

    const loadAcceptedQuestions = async () => {
      setActivityLoading(true);
      setActivityError("");
      setQuestionsModalUsername("");
      setQuestionsPopupPos(null);

      const variablesByFilter =
        activityFilter === "CUSTOM"
          ? { fromDate: customFromDate, toDate: customToDate }
          : buildPresetRange(activityFilter);

      try {
        const responses = await Promise.all(
          activeReportUsernames.map(async (username) => {
            try {
              const response = await apolloClient.query({
                query: LEETCODE_ACCEPTED_QUESTIONS_QUERY,
                variables: { username, ...variablesByFilter },
                fetchPolicy: "no-cache",
              });

              const report = response?.data?.leetcodeAcceptedQuestions;
              const questionMap = new Map();

              (report?.days || []).forEach((dayItem) => {
                (dayItem?.questions || []).forEach((question) => {
                  if (!question?.titleSlug) {
                    return;
                  }

                  if (!questionMap.has(question.titleSlug)) {
                    questionMap.set(question.titleSlug, {
                      title: question.title,
                      titleSlug: question.titleSlug,
                      date: question.date,
                    });
                  }
                });
              });

              const questions = Array.from(questionMap.values());

              return {
                username,
                totalCount: report?.totalCount ?? questions.length,
                questions,
              };
            } catch {
              return {
                username,
                totalCount: 0,
                questions: [],
              };
            }
          })
        );

        if (ignore) {
          return;
        }

        const nextStats = responses.reduce((acc, row) => {
          acc[row.username] = row;
          return acc;
        }, {});

        setCompletedStats(nextStats);
      } catch {
        if (!ignore) {
          setActivityError("Unable to fetch filtered completed questions.");
          setCompletedStats({});
        }
      } finally {
        if (!ignore) {
          setActivityLoading(false);
        }
      }
    };

    loadAcceptedQuestions();

    return () => {
      ignore = true;
    };
  }, [
    activeReportUsernames,
    activityFilter,
    apolloClient,
    customFromDate,
    customToDate,
    showReport,
  ]);

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id)
        ? prev.filter((s) => s !== id)
        : [...prev, id]
    );
  };

  const fetchSelectedStudents = () => {
    const selectedUsernames =
      fetchableStudents
        ?.filter((s) => selectedStudentIds.includes(s.id))
        ?.map((s) => s.leetCodeUname.trim()) || [];

    const uniqueUsernames = [...new Set(selectedUsernames)];
    setUsernames(uniqueUsernames);
    if (uniqueUsernames.length === 0) {
      setShowReport(false);
      return;
    }
    setShowReport(true);
  };

  const openQuestionsPopup = (username, buttonElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const gap = 12;
    const popupWidth = Math.min(window.innerWidth * 0.9, 420);
    const popupEstimatedHeight = 360;
    const minLeft = popupWidth / 2 + gap;
    const maxLeft = window.innerWidth - popupWidth / 2 - gap;
    const targetLeft = rect.left + rect.width / 2;
    const safeLeft = Math.min(Math.max(targetLeft, minLeft), maxLeft);

    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;

    let placement = "bottom";
    if (spaceBelow >= popupEstimatedHeight) {
      placement = "bottom";
    } else if (spaceAbove >= popupEstimatedHeight) {
      placement = "top";
    } else {
      placement = "left";
    }

    if (placement === "left" && rect.left < popupWidth + gap) {
      placement = spaceBelow >= spaceAbove ? "bottom" : "top";
    }

    const leftPlacementTop = Math.min(
      Math.max(rect.top + rect.height / 2, 110),
      window.innerHeight - 110
    );

    setQuestionsModalUsername(username);
    setQuestionsPopupPos({
      left: placement === "left" ? rect.left - gap : safeLeft,
      top: placement === "left" ? leftPlacementTop : placement === "top" ? rect.top - gap : rect.bottom + gap,
      placement,
    });
  };

  const extractSlugs = () => {
    let uniqueSlugs = [];
    
    const urlMatches = slugInput.matchAll(/\/problems\/([a-z0-9-]+)/g);
    const urlSlugs = [...urlMatches].map(m => m[1]);
    
    const titleMatches = slugInput.matchAll(/^\d+\.\s+(.+)$/gm);
    const titleSlugs = [...titleMatches].map(m => 
      m[1]
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
    );
    
    uniqueSlugs = [...new Set([...urlSlugs, ...titleSlugs])];
    setExtractedSlugs(uniqueSlugs);
  };

  const downloadCSV = () => {
    if (!searchedData.length) return;

    const escapeCsvValue = (value) => {
      const str = value ?? "";
      const normalized = String(str).replace(/"/g, '""');
      return `"${normalized}"`;
    };

    const headers = isFilteredView
      ? [
          "Roll No",
          "Student Name",
          "Solved Questions",
        ]
      : [
          "Roll No",
          "Student Name",
          "Total Questions",
          "Easy",
          "Medium",
          "Hard",
          "Profile",
        ];

    const rows = searchedData.map((s) => {
      const matchedStudent =
        studentData?.getClusterStudents?.find(
          (st) =>
            (st.leetCodeUname || "").trim().toLowerCase() ===
            (s.username || "").trim().toLowerCase()
        ) || {};

      if (isFilteredView) {
        const stat = completedStats[s.username];

        return [
          matchedStudent.rollNo || "-",
          matchedStudent.name || s.name || "-",
          stat?.totalCount ?? 0,
        ];
      }

      return [
        matchedStudent.rollNo || "-",
        matchedStudent.name || s.name || "-",
        s.totalSolved ?? 0,
        s.easySolved ?? 0,
        s.mediumSolved ?? 0,
        s.hardSolved ?? 0,
        s.profileUrl || "-",
      ];
    });

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "leetcode_report.csv";
    link.click();
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-white">
        <NetworkIndicator />
        <div className="p-6">
          <h1 className="mb-4 text-2xl font-bold text-gray-800">
            LeetCode Report
          </h1>
        </div>

        <div className="p-6 space-y-4 bg-white rounded shadow">

        {/* ✅ RADIO BUTTONS */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fetchMode"
              checked={selectedMode === "ALL"}
              onChange={() => {
                setSelectedMode("ALL");
                setSelectedStudentIds([]);
              }}
            />
            <span>Fetch All Students</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="fetchMode"
              checked={selectedMode === "SELECTED"}
              onChange={() => {
                setSelectedMode("SELECTED");
                setShowReport(false);
                setUsernames([]);
              }}
            />
            <span>Fetch Selected Students</span>
          </label>
        </div>

        {/* ✅ STUDENT SELECTION */}
        {selectedMode === "SELECTED" && (
          <div className="p-4 overflow-y-auto border rounded max-h-80">
            {fetchableStudents.length === 0 && (
              <p className="text-sm text-gray-600">No fetchable students found. Students must have a valid LeetCode username.</p>
            )}

            {fetchableStudents.map((s) => (
              <label
                key={s.id}
                className="flex gap-3 px-3 py-2 border-b cursor-pointer hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  checked={selectedStudentIds.includes(s.id)}
                  onChange={() => toggleStudent(s.id)}
                />
                <span>
                  {s.rollNo} - {s.name} ({s.leetCodeUname})
                </span>
              </label>
            ))}

            <button
              onClick={fetchSelectedStudents}
              disabled={selectedStudentIds.length === 0}
              className="px-4 py-2 mt-4 text-white bg-blue-600 rounded"
            >
              Fetch Report
            </button>
          </div>
        )}
        </div>

      {/* ✅ LOADING */}
      {loading && (
        <div className="flex items-center justify-center min-h-[calc(100vh-260px)]">
          <LightSpinner message="Fetching LeetCode data" />
        </div>
      )}

      {/* ✅ REPORT TABLE */}
      {showReport && displayedData.length > 0 && (
        <div className="p-6 mt-6 bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">
                    Date Filter
                  </label>
                  <select
                    value={activityFilter}
                    onChange={(e) => setActivityFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded"
                  >
                    {ACTIVITY_FILTERS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {activityFilter === "CUSTOM" && (
                  <>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        From
                      </label>
                      <input
                        type="date"
                        value={customFromDate}
                        onChange={(e) => setCustomFromDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        To
                      </label>
                      <input
                        type="date"
                        value={customToDate}
                        onChange={(e) => setCustomToDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-end justify-end gap-3">
              <div className="relative w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Search by name or roll number"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded sm:w-72 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => setSearchText("")}
                    className="absolute text-gray-400 transform -translate-y-1/2 right-3 top-1/2 hover:text-gray-600"
                  >
                    X
                  </button>
                )}
              </div>
              {/* <button
                onClick={() => setShowCompareList(!showCompareList)}
                className="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700"
              >
                Compare List
              </button> */}
              <div className="relative">
                <button
                  onClick={() => setShowDownloadDropdown((prev) => !prev)}
                  className="inline-flex items-center justify-center w-9 h-9 text-slate-600 transition-all hover:text-slate-900 focus:outline-none"
                  title="Download"
                  aria-label="Download"
                >
                  <FaDownload size={18} />
                </button>

                {showDownloadDropdown && (
                  <div className="absolute top-0 right-[calc(100%+10px)] z-20 w-[165px] rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_10px_24px_rgba(2,6,23,0.08)]">
                    <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                      Choose format
                    </p>
                    <button
                      onClick={() => {
                        downloadCSV();
                        setShowDownloadDropdown(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-slate-700 transition-colors hover:bg-slate-100"
                    >
                      <span className="rounded-lg bg-slate-100 p-1.5 text-slate-600">
                        <MdTableChart size={17} />
                      </span>
                      <span className="text-sm font-medium">CSV</span>
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>

            {activityError && (
              <p className="text-sm font-medium text-red-600">{activityError}</p>
            )}

            {showCompareList && (
              <div className="flex gap-2">
                <textarea
                  rows={2}
                  placeholder="Enter question number or paste your entire list"
                  value={slugInput}
                  onChange={e => setSlugInput(e.target.value)}
                  className="flex-1 p-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={extractSlugs}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
                >
                  Display Answer
                </button>
              </div>
            )}

            {extractedSlugs.length > 0 && (
              <div className="p-2 text-xs border border-gray-300 rounded bg-gray-50">
                <p className="mb-1 font-medium text-gray-600">
                  Extracted Slugs ({extractedSlugs.length}):
                </p>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {extractedSlugs.join('\n')}
                </div>
              </div>
            )}
          </div>

          {!isFilteredView && (
            <table className="w-full mt-4 border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Roll No</th>
                  <th className="p-2 border">Student Name</th>
                  <th className="p-2 border">Total Questions</th>
                  <th className="p-2 border">Easy</th>
                  <th className="p-2 border">Medium</th>
                  <th className="p-2 border">Hard</th>
                  {/* <th className="p-2 border">Rating</th> */}
                  <th className="p-2 border">Profile</th>
                </tr>
              </thead>

              <tbody>
                {searchedData.map((s, i) => {
                  const matchedStudent =
                    studentData?.getClusterStudents?.find(
                      (st) => st.leetCodeUname === s.username
                    );

                  return (
                    <tr key={s.username || i} className="text-center hover:bg-gray-50">
                      <td className="p-2 border">
                        {matchedStudent?.rollNo || "-"}
                      </td>
                      <td className="p-2 border">
                        {matchedStudent?.name || s.name || "-"}
                      </td>
                      <td className="p-2 border">{s.totalSolved}</td>
                      <td className="p-2 border">{s.easySolved}</td>
                      <td className="p-2 border">{s.mediumSolved}</td>
                      <td className="p-2 border">{s.hardSolved}</td>
                      {/* <td className="p-2 border">{s.contestRating}</td> */}
                      <td className="p-2 border">
                        <a
                          href={s.profileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600"
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  );
                })}
                {searchedData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-4 text-center text-gray-500 border">
                      No students found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {isFilteredView && (
            <div className="mt-4 overflow-hidden bg-white border shadow-sm border-slate-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-center border-b">Roll No</th>
                  <th className="p-3 text-center border-b">Student Name</th>
                  <th className="p-3 text-center border-b">Solved Questions</th>
                  <th className="p-3 text-center border-b">Question Names</th>
                  <th className="p-3 text-center border-b">Profile</th>
                </tr>
              </thead>

              <tbody>
                {searchedData.map((s, i) => {
                  const matchedStudent =
                    studentData?.getClusterStudents?.find(
                      (st) => st.leetCodeUname === s.username
                    );
                  const stat = completedStats[s.username];
                  const canExpand = (stat?.questions?.length || 0) > 0;

                  return (
                    <React.Fragment key={s.username || i}>
                      <tr className="text-center transition-colors hover:bg-slate-50">
                        <td className="p-3 border-b">{matchedStudent?.rollNo || "-"}</td>
                        <td className="p-3 border-b">
                          {matchedStudent?.name || s.name || "-"}
                        </td>
                        <td className="p-3 font-semibold border-b text-slate-700">
                          {activityLoading ? (
                            <span className="inline-block w-10 h-5 rounded animate-pulse bg-slate-200" />
                          ) : (
                            stat?.totalCount ?? 0
                          )}
                        </td>
                        <td className="p-3 border-b">
                          {activityLoading ? (
                            <span className="inline-block rounded-md w-28 h-9 animate-pulse bg-slate-200" />
                          ) : canExpand ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                openQuestionsPopup(s.username, e.currentTarget);
                              }}
                              className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-md border border-blue-100 hover:bg-blue-100"
                            >
                              View Questions
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled
                              title="No submitted questions"
                              className="px-3 py-1.5 text-sm font-medium text-slate-400 bg-slate-100 rounded-md border border-slate-200 cursor-not-allowed"
                            >
                              View Questions
                            </button>
                          )}
                        </td>
                        <td className="p-3 border-b">
                          <a
                            href={s.profileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600"
                          >
                            View
                          </a>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                {searchedData.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-gray-500 border-b">
                      No students found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}

          {questionsModalUsername && (
            <div className="fixed inset-0 z-50 pointer-events-none">
              <div
                className="absolute w-[92vw] max-w-md bg-white border border-gray-200 shadow-xl rounded-2xl pointer-events-auto"
                style={{
                  top: questionsPopupPos?.top || 96,
                  left: questionsPopupPos?.left || 260,
                  transform:
                    questionsPopupPos?.placement === "top"
                      ? "translate(-50%, -100%)"
                      : questionsPopupPos?.placement === "left"
                      ? "translate(-100%, -50%)"
                      : "translateX(-50%)",
                }}
              >
                {questionsPopupPos?.placement === "top" ? (
                  <span className="absolute w-3 h-3 bg-white border-r border-b border-gray-200 rotate-45 -bottom-1.5 left-1/2 -translate-x-1/2" />
                ) : questionsPopupPos?.placement === "left" ? (
                  <span className="absolute w-3 h-3 bg-white border-t border-r border-gray-200 rotate-45 -right-1.5 top-1/2 -translate-y-1/2" />
                ) : (
                  <span className="absolute w-3 h-3 bg-white border-t border-l border-gray-200 rotate-45 -top-1.5 left-1/2 -translate-x-1/2" />
                )}
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Question Names ({modalQuestions.length})
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setQuestionsModalUsername("");
                      setQuestionsPopupPos(null);
                    }}
                    className="inline-flex items-center justify-center w-8 h-8 text-gray-600 transition hover:text-gray-900"
                    aria-label="Close modal"
                  >
                    <LuX size={18} />
                  </button>
                </div>

                <div className="p-4 overflow-y-auto max-h-[320px]">
                  {modalQuestions.length > 0 ? (
                    <ul className="space-y-2">
                      {modalQuestions.map((question) => (
                        <li key={question.titleSlug}>
                        <a
                          href={`https://leetcode.com/problems/${question.titleSlug}/`}
                          target="_blank"
                          rel="noreferrer"
                          className="block px-3 py-2 text-sm font-medium text-left rounded-md text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                        >
                          {question.title}
                        </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-center text-gray-500">
                      No questions found.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default LeetcodeReportDetails;
